<!--
Copyright 2022 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license, please see LICENSE.md in the
project root for license information or contact permission@sei.cmu.edu for full terms.
-->

<div class="form-container">
  <mat-table [dataSource]="getDisplayedScoringCategories() | displayOrder" class="full-width full-height">
    <!-- Description Definition -->
    <ng-container matColumnDef="scoringCategoryDescription">
      <mat-header-cell *matHeaderCellDef class="scoresheet-toolbar">
        @if (haveSomeScoringCategories) {
        @if (showHeader) {
        <span class="view-buttons" role="group" aria-label="Displayed score">
          <button [matButton]="displaying === 'user' ? 'filled' : 'outlined'" (click)="selectDisplayedSubmission.emit('user')"
            [disabled]="myTeamId !== activeTeamId" title="Your personal score">
            User
          </button>
          <button [matButton]="displaying === 'team' ? 'filled' : 'outlined'" (click)="selectDisplayedSubmission.emit('team')"
            title="The team's score">
            Team
          </button>
          @if (!rightSideDisplay) {
          <button [matButton]="displaying === 'team-avg' ? 'filled' : 'outlined'"
            (click)="selectDisplayedSubmission.emit('team-avg')" [disabled]="myTeamId !== activeTeamId"
            title="The average of all user scores for your team">
            Team Avg
          </button>
          @if (showGroupAvgScore || displaying === 'group-avg') {
          <button [matButton]="displaying === 'group-avg' ? 'filled' : 'outlined'"
            (click)="selectDisplayedSubmission.emit('group-avg')" title="The average of all team scores for your group">
            Group Avg
          </button>
          }
          @if (showOfficialScore || displaying === 'official') {
          <button [matButton]="displaying === 'official' ? 'filled' : 'outlined'"
            (click)="selectDisplayedSubmission.emit('official')" title="The official score for this move">
            Official
          </button>
          }
          }
        </span>
        }
        @if (!selectedScoringModel.hideScoresOnScoreSheet) {
        <span class="title-font" [ngClass]="displayedScoreClass" title="{{ displayedScoreHover }}">{{
          displayedSubmission.score | number: '1.2-2' }}</span>
        }
        @if (!displayedSubmission.scoreIsAnAverage) {
        @let counts = getAnsweredCounts();
        <span class="answered-count">Answered {{ counts.answered }} of {{ counts.total }}</span>
        }
        @if (selectedScoringModel.useSubmit) {
        @if (myTeamId === activeTeamId) {
        <span class="toolbar-buttons">
          @if (showModifyControls) {
          <button matButton="outlined" (click)="presetSelections()" title="Preset selections to the previous move"
            [disabled]="noChanges">
            Preset
          </button>
          <button matButton="outlined" (click)="clearSelections()" title="Clear all selections"
            [disabled]="noChanges">
            Clear
          </button>
          }
          @if (showSubmitButton) {
          <button matButton="filled" (click)="completeSubmission()" title="Submit completed score"
            [disabled]="noChanges">
            Submit
          </button>
          }
          @if (showReopenButton) {
          <span class="status-font">Submitted</span>
          <button matButton="outlined" (click)="activateSubmission()" title="Reopen to change score"
            [disabled]="noChanges">
            Reopen
          </button>
          }
        </span>
        }
        @if (myTeamId !== activeTeamId && !displayedSubmission.scoreIsAnAverage) {
        <span class="status-font">{{ getSubmissionStatusText() }}</span>
        }
        }
        } @else {
        <div class="empty-move">
          <h2>No responses required for this move.</h2>
          @if (getNextQuestionMove(); as nextQuestionMove) {
          <p>Questions open in {{ nextQuestionMove }}.</p>
          }
        </div>
        }
      </mat-header-cell>
      <!-- scoresheet display -->
      <mat-cell *matCellDef="let scoringCategory" class="form-container" [ngClass]="tableClass">
        <div class="scoring-category-description" [ngClass]="tableClass">
          {{ scoringCategory.displayOrder }}. {{ scoringCategory.description }}
          @if (scoringCategory.calculationMethodId > 0) {
          <span>
            &nbsp;&nbsp;-&nbsp;&nbsp;[{{
            categoryScore(scoringCategory.id) | number: '1.0-0'
            }}]
          </span>
          }
        </div>
        <mat-table [dataSource]="scoringCategory.scoringOptions | displayOrder" class="full-width"
          [ngClass]="tableClass">
          <ng-container matColumnDef="scoringOptionDescription">
            <mat-cell *matCellDef="let scoringOption; let optionIndex = index" [ngClass]="tableClass">
              <div [ngClass]="tableClass" style="width: 100%;">
                @if (scoringCategory.scoringOptionSelection !== 'None') {
                <div [ngClass]="tableClass" class="option-row">
                  @if (!displayedSubmission.scoreIsAnAverage) {
                  <mat-checkbox [aria-label]="scoringOption.description"
                    [checked]="isSelected(scoringCategory.id, scoringOption.id)"
                    [disabled]="noChanges || !showModifyControls || myTeamId !== activeTeamId"
                    [matTooltip]="selectedBy(scoringCategory.id, scoringOption.id)" (change)="
                    toggleOption($event, scoringCategory.id, scoringOption.id)
                  " [ngClass]="tableClass">
                    @if (selectedScoringModel.displayCommentTextBoxes) {
                    <span class="question-number">{{ optionIndex + 1 }}.</span>
                    }
                    {{ scoringOption.description }} &nbsp;
                  </mat-checkbox>
                  }
                  @if (displayedSubmission.scoreIsAnAverage) {
                  <span>
                    [{{ selectedCount(scoringCategory.id, scoringOption.id) }}]
                    &nbsp;&nbsp;{{ scoringOption.description }}
                  </span>
                  }
                  @if (showModifyControls && myTeamId === activeTeamId && !selectedScoringModel.displayCommentTextBoxes)
                  {
                  <button id="add-comment" aria-label="Add Comment" mat-icon-button class="small-button"
                    (click)="addComment(addCommenttemplate, scoringOption)" title="Add Comment" [disabled]="noChanges">
                    <mat-icon class="small-icon" [ngClass]="tableClass" fontIcon="mdi-plus-box-outline"
                      aria-hidden="false"></mat-icon>
                  </button>
                  }
                </div>
                }
                @if (scoringCategory.scoringOptionSelection === 'None') {
                <div [ngClass]="tableClass">
                  @if (selectedScoringModel.displayCommentTextBoxes) {
                  <span class="question-number">{{ optionIndex + 1 }}.</span>
                  }
                  {{ scoringOption.description }}
                </div>
                }
                @if (!selectedScoringModel.displayCommentTextBoxes) {
                <div class="comment-list">
                  @for (
                  comment of optionComments(
                  scoringCategory.id,
                  scoringOption.id
                  )
                  ; track
                  comment.id) {
                  <div class="option-row">
                    {{ comment.comment }}
                    @if (displaying === 'team') {
                    <span> - {{ getUserName(comment.createdBy) }}</span>
                    }
                    @if (showModifyControls && myTeamId === activeTeamId) {
                    <button aria-label="Edit Comment" mat-icon-button class="small-button" (click)="
                      editComment(addCommenttemplate, scoringOption, comment)
                    " title="Edit Comment" [disabled]="noChanges">
                      <mat-icon class="icon-color small-icon" fontIcon="mdi-square-edit-outline"
                        aria-hidden="false"></mat-icon>
                    </button>
                    }
                    @if (showModifyControls && myTeamId === activeTeamId) {
                    <button aria-label="Delete Comment" mat-icon-button class="small-button"
                      (click)="deleteComment(comment)" title="Delete Comment" [disabled]="noChanges">
                      <mat-icon class="icon-color small-icon" fontIcon="mdi-trash-can-outline"
                        aria-hidden="false"></mat-icon>
                    </button>
                    }
                  </div>
                  }
                </div>
                }
                @if (selectedScoringModel.displayCommentTextBoxes) {
                <div class="comment-box">
                  <textarea matInput [ngModel]="optionComments(scoringCategory.id, scoringOption.id)[0]?.comment"
                    (change)="changeComment(scoringCategory.id, scoringOption.id, $event)" class="comment"
                    [disabled]="noChanges || (selectedScoringModel.useSubmit && !showSubmitButton)"
                    [attr.aria-label]="scoringOption.description"></textarea>
                  @switch (commentSaveStates[scoringOption.id]) {
                  @case ('saving') {
                  <div class="save-state">Saving…</div>
                  }
                  @case ('saved') {
                  <div class="save-state">Saved</div>
                  }
                  @case ('failed') {
                  <div class="save-state save-failed">Not saved</div>
                  }
                  }
                </div>
                }
              </div>
            </mat-cell>
          </ng-container>
          <ng-container matColumnDef="value">
            <mat-cell *matCellDef="let scoringOption" class="value-container" [ngClass]="tableClass">
              @if (!selectedScoringModel.hideScoresOnScoreSheet) {
              <div class="value-div">
                {{
                scoringCategory.calculationMethodId == 0 ||
                scoringOption.isModifier
                ? ' '
                : scoringOption.value
                }}
              </div>
              }
            </mat-cell>
          </ng-container>
          <mat-row *matRowDef="let row; columns: selectedScoringModel.hideScoresOnScoreSheet || scoringCategory.calculationMethodId == 0 ? ['scoringOptionDescription'] : ['scoringOptionDescription', 'value']"></mat-row>
        </mat-table>
      </mat-cell>
    </ng-container>
    <!-- Header and Row Declarations -->
    <mat-header-row *matHeaderRowDef="['scoringCategoryDescription']"></mat-header-row>
    <mat-row *matRowDef="let row; columns: ['scoringCategoryDescription']"></mat-row>
  </mat-table>

  @if (!selectedEvaluation.id || !selectedScoringModel) {
  <div class="text-center">
    <p>&nbsp;</p>
    <h1>An evaluation has not been properly configured.</h1>
    <h1>Please contact your administrator.</h1>
  </div>
  }
  @if (selectedEvaluation.id && selectedScoringModel && !activeTeamId) {
  <div class="text-center">
    <p>&nbsp;</p>
    <h1>You have not been given appropriate access to this application.</h1>
    <h1>Please contact your administrator.</h1>
  </div>
  }
</div>

<ng-template #addCommenttemplate>
  <crucible-dialog
    dialogTitle="Add a comment ..."
    [hideDefaultActions]="true"
    [guardUnsavedWork]="true"
  >
    <div crucibleDialogContent>
      <mat-form-field class="full-width">
        <mat-label>{{ commentOptionDescription }}</mat-label>
        <textarea matInput placeholder="" [(ngModel)]="currentComment"></textarea>
      </mat-form-field>
    </div>

    <div crucibleDialogActions>
      <button matButton="outlined" mat-dialog-close>Cancel</button>
      <button matButton="filled" [mat-dialog-close]="true" [disabled]="noChanges">Save</button>
    </div>
  </crucible-dialog>
</ng-template>
